/*
  A global Alert replacement.
  - Native: falls back to React Native Alert when no host is registered.
  - Web host: RN Modal (position:fixed) + themed card — not Paper Portal/Dialog,
    which uses absoluteFill and breaks on long scrolled forms.
  Usage: import { AppAlert } from '../services/AppAlert';
  Then call AppAlert.alert(title, message, buttons?, options?).
*/
import { Alert } from 'react-native';

export type AppAlertButton = {
  text: string;
  onPress?: () => void;
  style?: 'default' | 'cancel' | 'destructive';
};

export type AppAlertOptions = {
  cancelable?: boolean;
  onDismiss?: () => void;
};

export type AppAlertPayload = {
  title: string;
  message?: string;
  buttons: AppAlertButton[];
  options?: AppAlertOptions;
};

type HostListener = (payload: AppAlertPayload) => void;

let hostListener: HostListener | null = null;

export const AppAlert = {
  alert(
    title: string,
    message?: string,
    buttons?: AppAlertButton[],
    options?: AppAlertOptions,
  ) {
    const resolvedButtons =
      buttons && buttons.length > 0 ? buttons : [{ text: 'OK' }];
    if (hostListener) {
      hostListener({ title, message, buttons: resolvedButtons, options });
    } else {
      // Default to native Alert when host is not registered/enabled
      try {
        Alert.alert(
          title,
          message,
          resolvedButtons.map(b => ({
            text: b.text,
            onPress: b.onPress,
            style: b.style,
          })),
          {
            cancelable: options?.cancelable,
            onDismiss: options?.onDismiss,
          },
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[AppAlert] Native Alert failed:', e);
      }
    }
  },

  confirm(
    title: string,
    message?: string,
    options?: AppAlertOptions & { okText?: string; cancelText?: string },
  ): Promise<boolean> {
    const okText = options?.okText ?? 'OK';
    const cancelText = options?.cancelText ?? 'Cancel';
    return new Promise(resolve => {
      AppAlert.alert(
        title,
        message,
        [
          { text: cancelText, style: 'cancel', onPress: () => resolve(false) },
          { text: okText, style: 'default', onPress: () => resolve(true) },
        ],
        options,
      );
    });
  },

  registerHost(listener: HostListener) {
    hostListener = listener;
    return () => {
      if (hostListener === listener) hostListener = null;
    };
  },
};
